<template>
    <v-dialog v-model="dialog" persistent max-width="600px">
        <v-card class="elevation-1">
            <v-toolbar dark color="primary">
                <v-btn icon dark @click.native="handleModal">
                    <v-icon>fas fa-times-circle</v-icon>
                </v-btn>
                <v-toolbar-title>Reporte de venta por producto o categoria</v-toolbar-title>
                <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
                <v-row>
                    <v-col cols="6">
                        <SingleCalendar refElement="startDate" title="Fecha inicio"
                            :input-date.sync="reportParams.startDate" />
                    </v-col>
                    <v-col cols="6">
                        <SingleCalendar refElement="endDate" title="Fecha fin" :input-date.sync="reportParams.endDate"
                            :min-date="reportParams.startDate" />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" class="py-1">
                        <ValidationProvider rules="required" v-slot="{ errors, valid }" :immediate="true">
                            <v-autocomplete :items="productsCategoryMix" item-text="name" item-value="randomId"
                                v-model="reportParams.productsCategory" prepend-inner-icon="fas fa-boxes" clearable
                                hint="Seleccione una categoria o el producto." label="Producto/Categoria" required
                                outlined dense hide-details="auto" multiple :error-messages="errors" :success="valid">
                                <template v-slot:no-data>
                                    <v-list-item>
                                        <v-list-item-title>No hay categorias disponibles</v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-bind="attrs" v-on="on">
                                        <v-list-item-action>
                                            <v-checkbox :input-value="attrs.inputValue" :ripple="false"></v-checkbox>
                                        </v-list-item-action>

                                        <v-list-item-content>
                                            <v-list-item-title v-text="item.name"></v-list-item-title>
                                            <v-list-item-subtitle v-text="item.type"></v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </ValidationProvider>
                    </v-col>
                    <v-col cols="12" class="py-1">
                        <v-autocomplete :items="tipoSalida" item-text="nombre" item-value="id"
                            prepend-inner-icon="fas fa-outdent" hint="Selecciona el tipo de salida."
                            v-model="reportParams.tipoSalidaId" label="Tipo salida" required outlined multiple dense hide-details="auto">
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" class="py-1">
                        <v-autocomplete :items="sucursales" item-text="nombre" item-value="id"
                            v-model="reportParams.sucursalId" label="Sucursales" required outlined
                            prepend-inner-icon="fas fa-id-card-alt" :readonly="!canSeeAll"
                            hint="Seleccione la sucursal. Este campo es opcional" dense hide-details="auto" :clearable="canSeeAll">
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="12" class="py-1">
                        <v-autocomplete :items="clients" item-text="razonSocial" item-value="idCliente"
                            v-model="reportParams.clientId" label="Clientes" required outlined dense hide-details="auto"
                            prepend-inner-icon="fas fa-user-alt" hint="Seleccione un cliente. Este campo es opcional"
                            clearable>
                        </v-autocomplete>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-4" dark class="btn text-white" @click.prevent="previewPDF"
                :loading="loading" >
                    <v-icon left>fas fa-file-pdf</v-icon> {{ loading ? 'Generando PDF' : 'Ver PDF' }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <PreviewPDF :pdfSource="pdfSource" :pdfBlob="pdfBlob" title-pdf="REPORTE DE VENTAS POR PRODUCTO O CATEGORIA"
            :handlePdfModal="handlePdfModal" :dialog="showPdf" v-if="showPdf">
        </PreviewPDF>
    </v-dialog>
</template>
<script>
import PreviewPDF from '@/components/shared/PreviewPDF.vue';
import SingleCalendar from '@/components/shared/SingleCalendar.vue';
import SucursalDataService from '@/service/catalogo/sucursal/sucursalDataService';
import ProductoDataService from '@/service/producto/ProductoDataService';
import UsuarioDataService from '@/service/usuario/UsuarioDataService';
import VentaDataService from '@/service/venta/VentaDataService';
import { getDateLocaleNatural, getToday } from '@/utils/DateUtil';
import generateRandomUUID from '@/utils/UIDUtils';
import { template } from 'lodash';

export default {
    name: 'ExportSellByProductCategoryMix',
    props: {
        dialog: { type: Boolean, default: false },
        handleModal: { type: Function },
        tipoSalida: { type: Array, default: () => [] },
        sucursales: { type: Array, default: () => [] },
        clients: { type: Array, default: () => [] },
        isAdmin: { type: Boolean, default: false }
    },
    components: { SingleCalendar, PreviewPDF },
    created() {
        this.getProducts();
    },
    data() {
        return {
            reportParams: {
                startDate: getToday().toFormat('yyyy-MM-dd'),
                endDate: getToday().toFormat('yyyy-MM-dd'),
                tipoSalidaId: [],
                sucursalId: SucursalDataService.getCurrentSucursalId(),
                clientId: null,
                productsCategory: [],
            },
            productsCategoryMix: [],
            pdfBlob: '',
            pdfSource: null,
            showPdf: false,
            loading: false
        }
    },
    methods: {
        async getProducts() {
            this.productsCategoryMix = await ProductoDataService.findAllProductCategoryMix().then(result => {
                return result.data.map(item => {
                    return { ...item, randomId: generateRandomUUID() }
                })
            });
        },
        previewPDF() {
            const startDate = getDateLocaleNatural(this.reportParams.startDate) ?? '';
            const endDate = getDateLocaleNatural(this.reportParams.endDate) ?? '';
            let params = {
                startDate, endDate, productId: this.getProductId,
                productCategoryId: this.getProductCategoryId, tipoSalidaId: this.extractTypeOutId,
                sucursalId: this.reportParams.sucursalId, clientId: this.reportParams.clientId,
                typeOutNameSelected: this.extractTypeOutName
            };
            this.loading = true
            VentaDataService.printSellByCategoriesProduct(params).then(response => {
                if (response.status == 204) {
                    this.$swal.fire({ icon: 'info', title: 'Reporte de ventas por categoria', text: 'No hay registros para visualizar', showConfirmButton: false, timer: 2500 });
                    return
                }
                this.pdfBlob = new Blob([response.data], { type: 'application/pdf' })
                this.pdfSource = URL.createObjectURL(this.pdfBlob)
                this.showPdf = true
            }).catch(error => {
                this.$swal.fire({ position: 'top-end', icon: 'error', html: 'Error al generar el reporte' + error.response.data, showConfirmButton: false, timer: 3500 });
            }).finally(() => this.loading = false)
        },
        handlePdfModal() {
            this.showPdf = !this.showPdf
            this.cleanupPdf()
        },
        cleanupPdf() {
            if (this.pdfSource) {
                URL.revokeObjectURL(this.pdfSource)
                this.pdfSource = null
            }
            this.pdfBlob = null
        }
    },
    computed: {
        hasTypeOutSelected() {
            return this.reportParams.tipoSalidaId.length > 0
        },
        extractTypeOutName() {
            return this.hasTypeOutSelected ? this.tipoSalida.filter(ts => this.reportParams.tipoSalidaId.includes(ts.id)).map(item => item.nombre).join(', ') : 'General'
        },
        extractTypeOutId() {
            return this.hasTypeOutSelected ? this.reportParams.tipoSalidaId.join(',') : ''
        },
        canSeeAll() {
            const roles = UsuarioDataService.getUserRoles();
            return ['ROLE_COBRANZA', 'ROLE_ADMINISTRATOR_SUCURSAL'].some(role => roles.includes(role)) || this.isAdmin;
        },
        getProductCategorySelected() {
            return this.productsCategoryMix.filter(item => this.reportParams.productsCategory.includes(item.randomId));
        },
        getProductId() {
            const ids = this.getProductCategorySelected.filter(item => item.type === 'Producto').map(item => item.id).join(',');
            return ids.length > 0 ? ids : '';
        },
        getProductCategoryId() {
            const ids = this.getProductCategorySelected.filter(item => item.type === 'Categoria').map(item => item.id);
            return ids.length > 0 ? ids.join(',') : '';
        }
    }
}
</script>